import { ActivityV2 } from 'models/activityV2'
import { EventIcon } from 'components/NewCompanyPage/EventHistory/EventIcon'
import { formatEventTime } from 'components/NewCompanyPage/EventHistory/utils'

interface MobileEventItemProps {
  event: ActivityV2
}

/**
 * Generates a simple description for the event based on type and action.
 */
function getEventDescription(event: ActivityV2): string {
  const userName = event.user_name || 'System'
  const actionType = event.action_type
  const model = event.content_type?.model || ''

  // Helper to get field value
  const getFieldValue = (fieldName: string): string => {
    return (
      event.field_history_objects?.find((f) => f.field_name === fieldName)
        ?.value || ''
    )
  }

  // Handle different event types
  switch (model) {
    case 'reminder': {
      const title = getFieldValue('title')
      if (actionType === 'CREATE') {
        return title
          ? `Reminder sent to ${title}`
          : `Reminder created by ${userName}`
      }
      return `Reminder updated by ${userName}`
    }

    case 'companynote':
    case 'contactnote':
    case 'doornote': {
      if (actionType === 'CREATE') {
        return `Note added by ${userName}`
      }
      if (actionType === 'DELETE') {
        return `Note deleted by ${userName}`
      }
      return `Note updated by ${userName}`
    }

    case 'samplerequest': {
      const requestNumber = getFieldValue('request_number')
      if (actionType === 'CREATE') {
        return requestNumber
          ? `Sample request #${requestNumber} submitted`
          : `Sample request submitted by ${userName}`
      }
      // Check for cancellation
      const statusValue = Array.isArray(event.value)
        ? event.value[0]
        : event.value
      const parsedStatus =
        typeof statusValue === 'string' && statusValue.startsWith('"')
          ? JSON.parse(statusValue)
          : statusValue
      if (event.field_name === 'status' && parsedStatus === 'cancelled') {
        return `Sample request cancelled`
      }
      return `Sample request updated`
    }

    case 'deal': {
      if (actionType === 'CREATE') {
        return `Deal created by ${userName}`
      }
      if (actionType === 'DELETE') {
        return `Deal deleted by ${userName}`
      }
      // Handle specific field updates
      if (event.field_name === 'stage') {
        return `Deal stage updated by ${userName}`
      }
      return `Deal updated by ${userName}`
    }

    case 'emailmessage': {
      const subject = getFieldValue('subject')
      if (actionType === 'CREATE') {
        return subject ? `Email: ${subject}` : `Email received`
      }
      return `Email updated`
    }

    case 'document': {
      if (actionType === 'CREATE') {
        return `Document uploaded by ${userName}`
      }
      if (actionType === 'DELETE') {
        return `Document deleted by ${userName}`
      }
      return `Document updated by ${userName}`
    }

    case 'contactcompany': {
      // Account attributes changes
      if (event.field_name === 'tags') {
        if (actionType === 'ADD') {
          return `Tag added by ${userName}`
        }
        if (actionType === 'REMOVE') {
          return `Tag removed by ${userName}`
        }
      }
      if (actionType === 'UPDATE') {
        return `Account updated by ${userName}`
      }
      return `Account modified by ${userName}`
    }

    case 'contact': {
      if (actionType === 'CREATE') {
        return `Contact created by ${userName}`
      }
      if (actionType === 'DELETE') {
        return `Contact deleted by ${userName}`
      }
      return `Contact updated by ${userName}`
    }

    default: {
      if (actionType === 'CREATE') {
        return `Created by ${userName}`
      }
      if (actionType === 'DELETE') {
        return `Deleted by ${userName}`
      }
      if (actionType === 'UPDATE') {
        return `Updated by ${userName}`
      }
      return `Activity by ${userName}`
    }
  }
}

export function MobileEventItem({ event }: MobileEventItemProps) {
  const time = formatEventTime(event.created_at)
  const description = getEventDescription(event)

  return (
    <div className="flex items-center gap-3 py-2 px-1">
      {/* Icon */}
      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
        <EventIcon
          contentType={event.content_type}
          actionType={event.action_type}
        />
      </div>

      {/* Description */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-700 truncate">{description}</p>
      </div>

      {/* Time */}
      <div className="flex-shrink-0">
        <span className="text-xs text-gray-400">{time}</span>
      </div>
    </div>
  )
}
